import * as THREE from 'three';

export class Maze {
  constructor(scene, config) {
    this.scene = scene;
    this.config = config;
    this.walls = [];
    this.collisionBoxes = [];
    this.spatialGrid = null;
    this.gridCellSize = 10; // Size of each spatial grid cell
    this.useSpatialOptimization = false; // Enable for large mazes

    this.createFloor();
    this.createCeiling();
    this.createWalls();

    // Enable spatial optimization if many walls
    if (this.collisionBoxes.length > 50) {
      this.useSpatialOptimization = true;
      this.buildSpatialGrid();
      console.log(`Spatial optimization enabled for ${this.collisionBoxes.length} walls`);
    }
  }

  createFloor() {
    // Calculate floor size based on maze dimensions (with buffer)
    const size = this.calculateMazeSize();

    const floorGeometry = new THREE.PlaneGeometry(size, size);
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a1a1a,
      roughness: 0.3,
      metalness: 0.6,
    });

    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = 0;
    floor.receiveShadow = true;

    this.scene.add(floor);
  }

  createCeiling() {
    // Calculate ceiling size based on maze dimensions (with buffer)
    const size = this.calculateMazeSize();

    const ceilingGeometry = new THREE.PlaneGeometry(size, size);
    const ceilingMaterial = new THREE.MeshStandardMaterial({
      color: 0x0a0a0a,
      roughness: 0.8,
      metalness: 0.2,
      side: THREE.DoubleSide,
    });

    const ceiling = new THREE.Mesh(ceilingGeometry, ceilingMaterial);
    ceiling.rotation.x = Math.PI / 2;
    ceiling.position.y = 4;

    this.scene.add(ceiling);
  }

  /**
   * Calculate maze size from wall configurations
   * Returns size for floor/ceiling
   */
  calculateMazeSize() {
    if (!this.config.walls || this.config.walls.length === 0) {
      return 50; // Default size
    }

    // Find bounds from wall positions
    let maxExtent = 0;

    for (const wallData of this.config.walls) {
      const { position, dimensions } = wallData;
      const extent = Math.max(
        Math.abs(position.x) + (dimensions.width / 2),
        Math.abs(position.z) + (dimensions.width / 2)
      );
      maxExtent = Math.max(maxExtent, extent);
    }

    // Add buffer (20% extra)
    const size = Math.max(50, maxExtent * 2.4);

    return size;
  }

  createWalls() {
    // Create wall texture for visual interest
    const wallMaterial = new THREE.MeshStandardMaterial({
      color: 0x2a2a2a,
      roughness: 0.9,
      metalness: 0.1,
    });

    this.config.walls.forEach((wallData) => {
      const { position, rotation, dimensions } = wallData;

      // Create wall geometry
      const wallGeometry = new THREE.BoxGeometry(
        dimensions.width,
        dimensions.height,
        dimensions.depth
      );

      const wall = new THREE.Mesh(wallGeometry, wallMaterial);
      wall.position.set(position.x, position.y, position.z);
      wall.rotation.set(rotation.x, rotation.y, rotation.z);
      wall.castShadow = true;
      wall.receiveShadow = true;

      this.scene.add(wall);
      this.walls.push(wall);

      // Create collision box (slightly larger for better collision detection)
      const collisionBox = new THREE.Box3().setFromObject(wall);
      // Expand collision box slightly for player radius
      collisionBox.expandByScalar(0.5);
      this.collisionBoxes.push(collisionBox);
    });
  }

  /**
   * Build spatial grid for optimized collision detection
   * Divides world space into cells and assigns walls to cells
   */
  buildSpatialGrid() {
    this.spatialGrid = new Map();

    // Find world bounds
    let minX = Infinity, maxX = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;

    for (const box of this.collisionBoxes) {
      minX = Math.min(minX, box.min.x);
      maxX = Math.max(maxX, box.max.x);
      minZ = Math.min(minZ, box.min.z);
      maxZ = Math.max(maxZ, box.max.z);
    }

    // Assign each collision box to grid cells
    for (const box of this.collisionBoxes) {
      const cells = this.getGridCellsForBox(box);

      for (const cellKey of cells) {
        if (!this.spatialGrid.has(cellKey)) {
          this.spatialGrid.set(cellKey, []);
        }
        this.spatialGrid.get(cellKey).push(box);
      }
    }

    console.log(`Spatial grid built: ${this.spatialGrid.size} cells`);
  }

  /**
   * Get grid cell keys that a box overlaps
   */
  getGridCellsForBox(box) {
    const cells = [];

    const minCellX = Math.floor(box.min.x / this.gridCellSize);
    const maxCellX = Math.floor(box.max.x / this.gridCellSize);
    const minCellZ = Math.floor(box.min.z / this.gridCellSize);
    const maxCellZ = Math.floor(box.max.z / this.gridCellSize);

    for (let x = minCellX; x <= maxCellX; x++) {
      for (let z = minCellZ; z <= maxCellZ; z++) {
        cells.push(`${x},${z}`);
      }
    }

    return cells;
  }

  /**
   * Get grid cell key for a position
   */
  getGridCell(x, z) {
    const cellX = Math.floor(x / this.gridCellSize);
    const cellZ = Math.floor(z / this.gridCellSize);
    return `${cellX},${cellZ}`;
  }

  checkCollision(position) {
    // Create a bounding box for the player
    const playerBox = new THREE.Box3(
      new THREE.Vector3(position.x - 0.3, position.y - 1.6, position.z - 0.3),
      new THREE.Vector3(position.x + 0.3, position.y + 0.2, position.z + 0.3)
    );

    if (this.useSpatialOptimization) {
      // Optimized: Only check walls in nearby grid cells
      const cellKey = this.getGridCell(position.x, position.z);
      const nearbyBoxes = this.spatialGrid.get(cellKey) || [];

      // Also check adjacent cells for safety
      const adjacentCells = [
        this.getGridCell(position.x + this.gridCellSize, position.z),
        this.getGridCell(position.x - this.gridCellSize, position.z),
        this.getGridCell(position.x, position.z + this.gridCellSize),
        this.getGridCell(position.x, position.z - this.gridCellSize),
      ];

      const boxesToCheck = new Set(nearbyBoxes);
      for (const adjCell of adjacentCells) {
        const adjBoxes = this.spatialGrid.get(adjCell) || [];
        for (const box of adjBoxes) {
          boxesToCheck.add(box);
        }
      }

      // Check against nearby collision boxes only
      for (const box of boxesToCheck) {
        if (playerBox.intersectsBox(box)) {
          return true; // Collision detected
        }
      }
    } else {
      // Original: Check against all collision boxes
      for (const box of this.collisionBoxes) {
        if (playerBox.intersectsBox(box)) {
          return true; // Collision detected
        }
      }
    }

    return false; // No collision
  }

  dispose() {
    this.walls.forEach((wall) => {
      wall.geometry.dispose();
      wall.material.dispose();
      this.scene.remove(wall);
    });
    this.walls = [];
    this.collisionBoxes = [];
  }
}
